import { jwtDecode } from 'jwt-decode';
import { configureRefreshFetch, fetchJSON } from 'refresh-fetch';

import { env } from '@/config/env';
import { authenticatedAtom, store } from '@/lib/store';
import { PreemptiveRefreshError, isJwtExpiringSoon } from '@/utils/token';

import type { IProvider, UserInfo } from './provider-types';

const STORAGE_KEYS = {
  accessToken: 'omnihit.accessToken',
  refreshToken: 'omnihit.refreshToken',
};

const TOKEN_REFRESH_SKEW_MS = 30_000; // proactively refresh 30s before expiry

const ALLOWED_PARENT_ORIGINS = [
  'https://el-vendas.omnihit.app.br',
  'https://omnihitv2.omnihit.app.br',
  'https://co.omnihit.app.br',
];

let initialized = false;

const isOriginAllowed = (origin: string): boolean => {
  if (import.meta.env.DEV && origin.startsWith('http://localhost')) {
    return true;
  }
  return ALLOWED_PARENT_ORIGINS.includes(origin);
};

const getParentOrigin = (): string => {
  if (
    window.location.ancestorOrigins &&
    window.location.ancestorOrigins.length > 0
  ) {
    const origin = window.location.ancestorOrigins[0];
    return origin || '*';
  }
  return '*';
};

const requestCookieFromParent = (): Promise<string | null> => {
  return new Promise((resolve) => {
    const timeout = setTimeout(() => {
      window.removeEventListener('message', messageHandler);
      resolve(null);
    }, 5000);

    const messageHandler = (event: MessageEvent) => {
      if (!isOriginAllowed(event.origin)) {
        console.warn(
          'Rejected message from unauthorized origin:',
          event.origin
        );
        return;
      }

      if (event.data?.type === 'hitphone.cookieResponse') {
        clearTimeout(timeout);
        window.removeEventListener('message', messageHandler);
        resolve(event.data.cookie || null);
      }
    };

    window.addEventListener('message', messageHandler);

    sendParentMessage('hitphone.getCookie');
  });
};

const saveTokens = (tokens: { accessToken: string; refreshToken: string }) => {
  localStorage.setItem(STORAGE_KEYS.accessToken, tokens.accessToken);
  localStorage.setItem(STORAGE_KEYS.refreshToken, tokens.refreshToken);
};

const clearTokens = () => {
  localStorage.removeItem(STORAGE_KEYS.accessToken);
  localStorage.removeItem(STORAGE_KEYS.refreshToken);
};

const getAccessToken = () =>
  localStorage.getItem(STORAGE_KEYS.accessToken) || '';
const getRefreshToken = () =>
  localStorage.getItem(STORAGE_KEYS.refreshToken) || '';

const logout = async (): Promise<void> => {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return;

  try {
    await fetchJSON(`${env.VITE_OMNIHIT_BASE_AUTH_URL}/api/auth/logout`, {
      method: 'POST',
      body: JSON.stringify({ refreshToken }),
    });
  } catch {
    // Even if server-side invalidation fails, we still clear local session
  } finally {
    clearTokens();
    store.set(authenticatedAtom, false);
  }
};

const listeners = new Map<string, (event: MessageEvent) => void>();
const onParentMessage = (event: MessageEvent) => {
  if (!isOriginAllowed(event.origin)) return;
  const listener = listeners.get(event.data?.type as string);
  listener?.(event);
};

const sendParentMessage = (type: string, data?: Record<string, unknown>) => {
  window.parent.postMessage({ type, ...data }, getParentOrigin());
};

const normalizeUrl = (url: string) => {
  return url.startsWith('/') ? url : `/${url}`;
};

const fetchJSONWithToken = async (
  url: RequestInfo | URL,
  options: RequestInit = {}
) => {
  const token = getAccessToken();

  if (token && isJwtExpiringSoon(token, TOKEN_REFRESH_SKEW_MS)) {
    throw new PreemptiveRefreshError();
  }
  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string>),
  };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const fullUrl = `${env.VITE_OMNIHIT_BASE_AUTH_URL}${normalizeUrl(String(url))}`;
  return fetchJSON(fullUrl, { ...options, headers });
};

const shouldRefreshToken = (error: unknown) => {
  if (error instanceof PreemptiveRefreshError) return true;
  const status = (error as { response?: Response })?.response?.status;
  return status === 401 || status === 403;
};

const doRefreshToken = async () => {
  const refreshToken = getRefreshToken();

  if (!refreshToken) {
    clearTokens();
    return Promise.reject(new Error('no-refresh-token'));
  }

  try {
    const response = await fetchJSON(
      `${env.VITE_OMNIHIT_BASE_AUTH_URL}/api/auth/refresh`,
      {
        method: 'POST',
        body: JSON.stringify({ refreshToken }),
      }
    );

    const body = response.body as { accessToken: string; refreshToken: string };
    const tokens = {
      accessToken: body.accessToken,
      refreshToken: body.refreshToken,
    };

    saveTokens(tokens);
  } catch (error) {
    const status = (error as { response?: Response })?.response?.status;
    if (status === 401 || status === 403) {
      clearTokens();
    }
    throw error;
  }
};

export const omnihitAuthRefreshFetch = configureRefreshFetch({
  fetch: fetchJSONWithToken,
  shouldRefreshToken,
  refreshToken: doRefreshToken,
});

export const omnihitProvider: IProvider = {
  id: 'omnihit',

  async initialize() {
    if (initialized) return;
    initialized = true;

    window.addEventListener('message', onParentMessage);
    listeners.set('hitphone.userLoggedOut', logout);
  },

  async authenticate() {
    const refreshToken = getRefreshToken();
    if (refreshToken) {
      const isConsistent = await (async () => {
        let sub: string | undefined;
        try {
          const decoded = jwtDecode<{ sub?: string }>(refreshToken);
          sub = decoded.sub;
        } catch {
          return false;
        }

        const cwUserId = await (async () => {
          return new Promise<string | null>((resolve) => {
            const timeout = setTimeout(() => {
              window.removeEventListener('message', messageHandler);
              resolve(null);
            }, 5000);

            const messageHandler = (event: MessageEvent) => {
              if (!isOriginAllowed(event.origin)) {
                console.warn(
                  'Rejected message from unauthorized origin:',
                  event.origin
                );
                return;
              }
              if (event.data?.type === 'hitphone.userIdResponse') {
                clearTimeout(timeout);
                window.removeEventListener('message', messageHandler);
                resolve(event.data?.userId ?? null);
              }
            };

            window.addEventListener('message', messageHandler);

            sendParentMessage('hitphone.getCurrentUserId');
          });
        })();

        if (!cwUserId) return false;
        return String(sub) === String(cwUserId);
      })();

      if (isConsistent) {
        store.set(authenticatedAtom, true);
        return;
      }

      await logout();
    }

    const cwCookie = await requestCookieFromParent();

    if (!cwCookie) {
      clearTokens();
      store.set(authenticatedAtom, false);
      throw new Error('cw-cookie-not-found');
    }
    let idToken = '';
    let client = '';
    let uid = '';
    try {
      const parsed = JSON.parse(decodeURIComponent(cwCookie));
      idToken = parsed?.['access-token'] || '';
      client = parsed?.client || '';
      uid = parsed?.uid || '';
    } catch {
      store.set(authenticatedAtom, false);
      throw new Error('cw-cookie-parse-failed');
    }
    if (!idToken) throw new Error('id-token-missing');

    const { body }: { body: { accessToken: string; refreshToken: string } } =
      await fetchJSON(
        `${env.VITE_OMNIHIT_BASE_AUTH_URL}/api/auth/sso/omnihit`,
        {
          method: 'POST',
          body: JSON.stringify({ idToken, client, uid }),
        }
      );
    const tokens = {
      accessToken: body.accessToken,
      refreshToken: body.refreshToken,
    };
    saveTokens(tokens);
    store.set(authenticatedAtom, true);
  },

  async getAuthHeaders(): Promise<Record<string, string>> {
    const token = getAccessToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  },

  sendExtensionReadyNotification: () => {
    sendParentMessage('hitphone.extensionReady');
  },

  setCallListener: (listener: (number: string) => void) => {
    listeners.set('hitphone.call', (event) =>
      listener(event.data?.number as string)
    );
  },

  openTicket: async (ticketId: string) => {
    try {
      const response = await omnihitAuthRefreshFetch(
        `/api/crm/sap/ticket-url?ticket=${ticketId}`
      );
      const { url } = response.body as { url: string };

      sendParentMessage('hitphone.openUrl', {
        url,
        name: 'sap-ticket',
      });
    } catch (error) {
      console.error('Failed to open ticket:', error);
      throw error;
    }
  },

  openWidget: () => {
    sendParentMessage('hitphone.openWidget');
  },

  async getUserInfo(): Promise<UserInfo> {
    const accessToken = getAccessToken();

    if (!accessToken) {
      throw new Error('No access token available');
    }

    try {
      const decoded = jwtDecode<{ email?: string }>(accessToken);

      if (!decoded.email) {
        throw new Error('Email claim not found in access token');
      }

      return { email: decoded.email };
    } catch (error) {
      throw new Error(`Failed to decode access token: ${error}`);
    }
  },
};
